<script setup lang="ts">
/**
 * What can be done to the selected part of a table.
 *
 * One toolbar for every scope rather than one each. Whatever is selected, a
 * cell, a row, a column, a block of cells or the whole table, the same
 * controls set it: alignment, emphasis, the two colours, merging, and the rows
 * and columns around it. The controls that make no sense for a scope are left
 * out rather than greyed, so a selected column is not offered "row above" to
 * wonder about, and a single plain cell is not offered a merge.
 *
 * Merging comes first when it is offered, because a block of cells is chosen
 * to be merged far more often than to be coloured, and the merge says before
 * it is made how many texts it would drop.
 *
 * Every control shows the state the selection actually has, and shows nothing
 * where the selected cells disagree. A pressed "centred" over a row that is
 * half centred would be a lie, and somebody who then "leaves it" has changed
 * nothing when they meant to and everything when they did not.
 *
 * Says nothing about how a change is made. It reports what was asked for and
 * the inspector, which owns the table, applies it through `edit.ts`.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { CellAlign } from '@/domain/section'
import CellColourPicker from '@/sections/table/CellColourPicker.vue'
import {
  CELL_ALIGNMENTS,
  CELL_FILL_IDS,
  CELL_FILLS,
  CELL_INK_IDS,
  CELL_INKS,
  type CellStyle,
  type CellTarget,
} from '@/sections/table/edit'
import { lineOf } from '@/sections/table/spans'

const { t } = useI18n()

const props = defineProps<{
  target: CellTarget
  /** What the targeted cells share. */
  shared: CellStyle
  /** Whether the targeted column holds figures. Meaningless for a row. */
  numeric: boolean
  /** How many body rows and columns the table has, for the bounds. */
  rows: number
  columns: number
  /** Which ways the selected row or column can move without tearing a merged cell. */
  movable: { up: boolean; down: boolean; left: boolean; right: boolean }
  /** Whether the selection holds several cells that can become one. */
  mergeable: boolean
  /** Whether the selection holds a merged cell. */
  splittable: boolean
  /** How many texts merging the selection would drop. */
  lost: number
  /**
   * The table's words and layout are somebody else's (CSRD's): only how they
   * look is offered, since adding a row or merging two cells would put the
   * table out of step with its source.
   */
  locked?: boolean
}>()

const emit = defineEmits<{
  align: [align: CellAlign | null]
  strong: [on: boolean]
  ruleAbove: [on: boolean]
  ruleBelow: [on: boolean]
  fill: [colour: string | null]
  ink: [colour: string | null]
  numeric: [on: boolean]
  insertRow: [where: 'above' | 'below']
  insertColumn: [where: 'left' | 'right']
  moveRow: [by: -1 | 1]
  moveColumn: [by: -1 | 1]
  removeRow: []
  removeColumn: []
  clear: []
  merge: []
  split: []
  deselect: []
}>()

/** The alignment icons: three lines, ragged the way the text would be. */
const ALIGN_ICONS: Record<CellAlign, readonly string[]> = {
  left: ['M2 4h12', 'M2 8h8', 'M2 12h10'],
  center: ['M2 4h12', 'M4 8h8', 'M3 12h10'],
  right: ['M2 4h12', 'M6 8h8', 'M4 12h10'],
}

const label = computed(() => {
  const { target } = props
  switch (target.scope) {
    case 'cell':
      return target.row === 'header'
        ? t('inspector.table.selection.headerCell', { column: target.column + 1 })
        : t('inspector.table.selection.cell', { row: target.row + 1, column: target.column + 1 })
    case 'row':
      return target.row === 'header'
        ? t('inspector.table.selection.header')
        : t('inspector.table.selection.row', { row: target.row + 1 })
    case 'column':
      return t('inspector.table.selection.column', { column: target.column + 1 })
    case 'range':
      return t('inspector.table.selection.range', {
        rows: Math.abs(lineOf(target.from.row) - lineOf(target.to.row)) + 1,
        columns: Math.abs(target.from.column - target.to.column) + 1,
      })
    case 'table':
      return t('inspector.table.selection.table')
  }
})

/** The body row the target sits on, if it sits on one. */
const row = computed<number | null>(() => {
  const { target } = props
  if (target.scope !== 'cell' && target.scope !== 'row') return null
  return target.row === 'header' ? null : target.row
})

const onHeader = computed(() => {
  const { target } = props
  return (target.scope === 'cell' || target.scope === 'row') && target.row === 'header'
})

const column = computed<number | null>(() => {
  const { target } = props
  return target.scope === 'cell' || target.scope === 'column' ? target.column : null
})

const hasRowOps = computed(() => row.value !== null || onHeader.value)
const hasColumnOps = computed(() => column.value !== null)

const fills = computed(() =>
  CELL_FILL_IDS.map((id) => ({
    id,
    colour: CELL_FILLS[id],
    name: t(`inspector.table.fills.${id}`),
  })),
)

const inks = computed(() =>
  CELL_INK_IDS.map((id) => ({ id, colour: CELL_INKS[id], name: t(`inspector.table.inks.${id}`) })),
)

/**
 * Why a move button is off, when a merge is the reason.
 *
 * Only then: a first row that cannot go up needs no explaining, a row held by
 * a merged cell does, or the button reads as broken.
 */
function tiedTitle(neighbour: boolean, movable: boolean, key: 'tiedRow' | 'tiedColumn'): string {
  return neighbour && !movable ? t(`inspector.table.${key}`) : ''
}

/**
 * The pictograms of the rows' and columns' tools, drawn on a 16-unit square:
 * a band with a plus on the side it is added, an arrow the way it moves, a bin.
 */
const TOOL_ICONS = {
  rowAbove: ['M2.5 9.5h11v4h-11z', 'M8 2.5v5M5.5 5h5'],
  rowBelow: ['M2.5 2.5h11v4h-11z', 'M8 8.5v5M5.5 11h5'],
  up: ['M8 13V3.5M4.5 7l3.5-3.5L11.5 7'],
  down: ['M8 3v9.5M4.5 9l3.5 3.5L11.5 9'],
  columnLeft: ['M9.5 2.5h4v11h-4z', 'M2.5 8h5M5 5.5v5'],
  columnRight: ['M2.5 2.5h4v11h-4z', 'M8.5 8h5M11 5.5v5'],
  left: ['M13 8H3.5M7 4.5L3.5 8 7 11.5'],
  right: ['M3 8h9.5M9 4.5l3.5 3.5L9 11.5'],
  bin: ['M3 4.5h10M5.5 4.5V3h5v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5'],
} as const

/** Pressing the alignment already set takes it off, back to the cell's kind. */
function align(choice: CellAlign): void {
  emit('align', props.shared.align === choice ? null : choice)
}
</script>

<template>
  <div class="toolbar">
    <div class="toolbar__head">
      <span class="toolbar__label">{{ label }}</span>
      <button
        type="button"
        class="toolbar__deselect"
        :title="t('inspector.table.deselect')"
        @click="emit('deselect')"
      >
        &times;
        <span class="visually-hidden">{{ t('inspector.table.deselect') }}</span>
      </button>
    </div>

    <div v-if="!locked && (mergeable || splittable)" class="ops">
      <span class="ops__label">{{ t('inspector.table.merging') }}</span>
      <div class="ops__buttons">
        <button
          v-if="mergeable"
          type="button"
          class="op op--primary"
          :title="t('inspector.table.mergeHint')"
          @click="emit('merge')"
        >
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <path
              d="M2.5 3.5h11v9h-11zM5 8h6M9.2 6.2 11 8l-1.8 1.8M6.8 6.2 5 8l1.8 1.8"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ t('inspector.table.merge') }}
        </button>
        <button
          v-if="splittable"
          type="button"
          class="op op--icon"
          :title="t('inspector.table.splitHint')"
          @click="emit('split')"
        >
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <path
              d="M2.5 3.5h11v9h-11zM8 3.5v9M3.8 8h2.4M5 6.8 6.2 8 5 9.2M12.2 8H9.8M11 6.8 9.8 8 11 9.2"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ t('inspector.table.split') }}
        </button>
        <p v-if="mergeable && lost > 0" class="ops__note">
          {{ t('inspector.table.mergeLoses', { count: lost }, lost) }}
        </p>
      </div>
    </div>

    <div class="toolbar__row">
      <div class="segments" role="radiogroup" :aria-label="t('inspector.table.align')">
        <button
          v-for="choice in CELL_ALIGNMENTS"
          :key="choice"
          type="button"
          class="segment"
          :class="{ 'segment--on': shared.align === choice }"
          :aria-pressed="shared.align === choice"
          :title="t(`inspector.table.aligns.${choice}`)"
          @click="align(choice)"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path
              v-for="(d, at) in ALIGN_ICONS[choice]"
              :key="at"
              :d="d"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
          <span class="visually-hidden">{{ t(`inspector.table.aligns.${choice}`) }}</span>
        </button>
      </div>

      <button
        type="button"
        class="toggle"
        :class="{ 'toggle--on': shared.strong }"
        :aria-pressed="shared.strong === true"
        :title="t('inspector.table.strongHint')"
        @click="emit('strong', !shared.strong)"
      >
        <b>B</b>
        {{ t('inspector.table.strong') }}
      </button>

      <button
        type="button"
        class="toggle"
        :class="{ 'toggle--on': shared.ruleAbove }"
        :aria-pressed="shared.ruleAbove === true"
        :title="t('inspector.table.ruleAboveHint')"
        @click="emit('ruleAbove', !shared.ruleAbove)"
      >
        <span class="toggle__rule toggle__rule--above" aria-hidden="true"></span>
        {{ t('inspector.table.ruleAbove') }}
      </button>

      <button
        type="button"
        class="toggle"
        :class="{ 'toggle--on': shared.ruleBelow }"
        :aria-pressed="shared.ruleBelow === true"
        :title="t('inspector.table.ruleBelowHint')"
        @click="emit('ruleBelow', !shared.ruleBelow)"
      >
        <span class="toggle__rule toggle__rule--below" aria-hidden="true"></span>
        {{ t('inspector.table.ruleBelow') }}
      </button>

      <button
        v-if="hasColumnOps"
        type="button"
        class="toggle"
        :class="{ 'toggle--on': numeric }"
        :aria-pressed="numeric"
        :title="t('inspector.table.markNumeric')"
        @click="emit('numeric', !numeric)"
      >
        <span class="toggle__figures">123</span>
        {{ t('inspector.table.numeric') }}
      </button>
    </div>

    <CellColourPicker
      :label="t('inspector.table.fill')"
      :options="fills"
      :chosen="shared.fill"
      :none-label="t('inspector.table.noColour')"
      :custom-label="t('inspector.table.customColour')"
      @choose="(colour) => emit('fill', colour)"
    />

    <CellColourPicker
      :label="t('inspector.table.ink')"
      :options="inks"
      :chosen="shared.ink"
      :none-label="t('inspector.table.noColour')"
      :custom-label="t('inspector.table.customColour')"
      @choose="(colour) => emit('ink', colour)"
    />

    <div v-if="!locked && hasRowOps" class="ops">
      <span class="ops__label">{{ t('inspector.table.rows') }}</span>
      <div class="tools">
        <div class="tools__group">
          <button
            v-if="!onHeader"
            type="button"
            class="tool"
            :title="t('inspector.table.rowAbove')"
            :aria-label="t('inspector.table.rowAbove')"
            @click="emit('insertRow', 'above')"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path v-for="(d, at) in TOOL_ICONS.rowAbove" :key="at" :d="d" />
            </svg>
          </button>
          <button
            type="button"
            class="tool"
            :title="t('inspector.table.rowBelow')"
            :aria-label="t('inspector.table.rowBelow')"
            @click="emit('insertRow', 'below')"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path v-for="(d, at) in TOOL_ICONS.rowBelow" :key="at" :d="d" />
            </svg>
          </button>
        </div>
        <template v-if="row !== null">
          <div class="tools__group">
            <button
              type="button"
              class="tool"
              :disabled="!movable.up"
              :title="tiedTitle(row > 0, movable.up, 'tiedRow') || t('inspector.table.moveUp')"
              :aria-label="t('inspector.table.moveUp')"
              @click="emit('moveRow', -1)"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path v-for="(d, at) in TOOL_ICONS.up" :key="at" :d="d" />
              </svg>
            </button>
            <button
              type="button"
              class="tool"
              :disabled="!movable.down"
              :title="
                tiedTitle(row < rows - 1, movable.down, 'tiedRow') || t('inspector.table.moveDown')
              "
              :aria-label="t('inspector.table.moveDown')"
              @click="emit('moveRow', 1)"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path v-for="(d, at) in TOOL_ICONS.down" :key="at" :d="d" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            class="tool tool--drop"
            :title="t('inspector.table.deleteRow')"
            :aria-label="t('inspector.table.deleteRow')"
            @click="emit('removeRow')"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path v-for="(d, at) in TOOL_ICONS.bin" :key="at" :d="d" />
            </svg>
          </button>
        </template>
      </div>
    </div>

    <div v-if="!locked && hasColumnOps && column !== null" class="ops">
      <span class="ops__label">{{ t('inspector.table.columns') }}</span>
      <div class="tools">
        <div class="tools__group">
          <button
            type="button"
            class="tool"
            :title="t('inspector.table.columnLeft')"
            :aria-label="t('inspector.table.columnLeft')"
            @click="emit('insertColumn', 'left')"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path v-for="(d, at) in TOOL_ICONS.columnLeft" :key="at" :d="d" />
            </svg>
          </button>
          <button
            type="button"
            class="tool"
            :title="t('inspector.table.columnRight')"
            :aria-label="t('inspector.table.columnRight')"
            @click="emit('insertColumn', 'right')"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path v-for="(d, at) in TOOL_ICONS.columnRight" :key="at" :d="d" />
            </svg>
          </button>
        </div>
        <div class="tools__group">
          <button
            type="button"
            class="tool"
            :disabled="!movable.left"
            :title="
              tiedTitle(column > 0, movable.left, 'tiedColumn') || t('inspector.table.moveLeft')
            "
            :aria-label="t('inspector.table.moveLeft')"
            @click="emit('moveColumn', -1)"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path v-for="(d, at) in TOOL_ICONS.left" :key="at" :d="d" />
            </svg>
          </button>
          <button
            type="button"
            class="tool"
            :disabled="!movable.right"
            :title="
              tiedTitle(column < columns - 1, movable.right, 'tiedColumn') ||
              t('inspector.table.moveRight')
            "
            :aria-label="t('inspector.table.moveRight')"
            @click="emit('moveColumn', 1)"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path v-for="(d, at) in TOOL_ICONS.right" :key="at" :d="d" />
            </svg>
          </button>
        </div>
        <button
          type="button"
          class="tool tool--drop"
          :disabled="columns <= 1"
          :title="t('inspector.table.deleteColumn')"
          :aria-label="t('inspector.table.deleteColumn')"
          @click="emit('removeColumn')"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path v-for="(d, at) in TOOL_ICONS.bin" :key="at" :d="d" />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="!locked" class="ops">
      <span class="ops__label" />
      <div class="ops__buttons">
        <button
          type="button"
          class="op op--drop"
          :title="t('inspector.table.clearHint')"
          @click="emit('clear')"
        >
          {{ t('inspector.table.clear') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.toolbar {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding: var(--sp-4);
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.toolbar__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
}

.toolbar__label {
  color: var(--accent);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.toolbar__deselect {
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-faint);
  font: inherit;
  font-size: var(--text-md);
  line-height: 1;
  cursor: pointer;
}

.toolbar__deselect:hover {
  background: var(--panel-hover);
  color: var(--ink);
}

.toolbar__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-3);
}

.segments {
  display: flex;
  gap: 1px;
  padding: 2px;
  border-radius: var(--radius-xs);
  background: var(--panel);
  box-shadow: inset 0 0 0 1px var(--hairline-strong);
}

.segment {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.segment:hover {
  color: var(--ink);
}

.segment--on {
  background: var(--accent-quiet);
  color: var(--accent);
}

.toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  height: 26px;
  padding: 0 var(--sp-4);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-xs);
  background: var(--panel);
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick),
    border-color var(--quick);
}

.toggle:hover {
  color: var(--ink);
}

.toggle--on {
  border-color: transparent;
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}

.toggle__rule {
  display: inline-block;
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  border: 1px dotted var(--ink-faint);
}

.toggle__rule--above {
  border-top: 2px solid currentColor;
}

.toggle__rule--below {
  border-bottom: 2px solid currentColor;
}

.toggle__figures {
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.ops {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-4);
}

.ops__label {
  flex: none;
  width: 56px;
  padding-top: 7px;
  color: var(--ink-soft);
  font-size: var(--text-xs);
}

.ops__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
}

.op {
  height: 24px;
  padding: 0 var(--sp-4);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-xs);
  background: var(--panel);
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-sm);
  white-space: nowrap;
  cursor: pointer;
  transition:
    color var(--quick),
    border-color var(--quick);
}

.op:hover:not(:disabled) {
  border-color: var(--ink-faint);
  color: var(--ink);
}

/* The merge, when offered, is what a block of cells was most likely chosen
   for, so it is the one button here drawn in the accent. */
.op--primary,
.op--icon {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
}

.op--primary {
  border-color: var(--accent-line);
  color: var(--accent);
  font-weight: 600;
}

.op--primary:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--accent);
}

.op svg {
  flex: none;
}

.ops__note {
  flex-basis: 100%;
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.45;
}

.op--drop:hover:not(:disabled) {
  border-color: var(--danger);
  color: var(--danger);
}

.op:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* A row's or a column's tools: pictograms in small groups, adding and moving
   apart from each other and from the bin, one line whatever the language. */
.tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-3);
}

.tools__group {
  display: inline-flex;
  padding: 1px;
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel);
}

.tool {
  display: grid;
  place-items: center;
  width: 28px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.tool svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.35;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.tool:hover:not(:disabled) {
  background: var(--accent-quiet);
  color: var(--accent);
}

.tool--drop {
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel);
}

.tool--drop:hover:not(:disabled) {
  border-color: var(--danger);
  background: none;
  color: var(--danger);
}

.tool:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
